import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api } from "../../lib/api";
import { AdminLayout } from "./AdminLayout";

export default function AdminSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [state, setState] = useState({ bookings_open: true, blackout_dates: [], consultation_note: "" });
  const [newDate, setNewDate] = useState("");

  useEffect(() => { if (data) setState({ bookings_open: data.bookings_open ?? true, blackout_dates: data.blackout_dates || [], consultation_note: data.consultation_note || "" }); }, [data]);

  const save = async (patch) => {
    const next = { ...state, ...patch };
    setState(next);
    try {
      await api.put("/admin/settings", next);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch { toast.error("Save failed"); }
  };

  const addBlackout = () => {
    if (!newDate || state.blackout_dates.includes(newDate)) return;
    save({ blackout_dates: [...state.blackout_dates, newDate].sort() });
    setNewDate("");
  };

  return (
    <AdminLayout title="Booking Settings">
      <div className="max-w-2xl space-y-8">
        <div className="border border-ash bg-charcoal p-6 flex items-center justify-between" data-testid="settings-bookings-open">
          <div>
            <h3 className="font-serif text-2xl text-white">Bookings open</h3>
            <p className="text-[#828290] text-sm mt-1">When off, the public booking form is disabled.</p>
          </div>
          <button onClick={() => save({ bookings_open: !state.bookings_open })} data-testid="toggle-bookings"
            className={`relative w-16 h-8 border transition-colors ${state.bookings_open ? "bg-ember border-ember" : "bg-obsidian border-ash"}`}>
            <span className={`absolute top-1 w-6 h-6 transition-all ${state.bookings_open ? "left-9 bg-obsidian" : "left-1 bg-[#828290]"}`} />
          </button>
        </div>

        <div className="border border-ash bg-charcoal p-6">
          <h3 className="font-serif text-2xl text-white mb-1">Blackout dates</h3>
          <p className="text-[#828290] text-sm mb-5">Dates the studio is unavailable.</p>
          <div className="flex gap-3 mb-4">
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="blackout-input" className="bg-obsidian border border-ash text-white px-4 py-2.5 outline-none focus:border-ember" />
            <button onClick={addBlackout} data-testid="blackout-add" className="inline-flex items-center gap-1 border border-ash text-white px-4 font-mono text-xs uppercase tracking-widest hover:border-ember"><Plus size={14} /> Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {state.blackout_dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-2 border border-ash text-[#C4C4CC] px-3 py-1.5 font-mono text-xs">
                {d}<button onClick={() => save({ blackout_dates: state.blackout_dates.filter((x) => x !== d) })} className="text-[#828290] hover:text-red-400"><X size={12} /></button>
              </span>
            ))}
            {state.blackout_dates.length === 0 && <span className="text-[#555562] text-sm">None set.</span>}
          </div>
        </div>

        <div className="border border-ash bg-charcoal p-6">
          <h3 className="font-serif text-2xl text-white mb-1">Consultation note</h3>
          <p className="text-[#828290] text-sm mb-5">Shown to clients about consultations and deposits.</p>
          <textarea rows={3} value={state.consultation_note} onChange={(e) => setState({ ...state, consultation_note: e.target.value })}
            className="w-full bg-obsidian border border-ash text-white px-4 py-3 outline-none focus:border-ember resize-none" data-testid="consultation-note" />
          <button onClick={() => save({})} className="mt-4 bg-ember text-obsidian font-mono text-xs uppercase tracking-widest font-semibold px-6 py-3 hover:bg-white transition-colors">Save</button>
        </div>
      </div>
    </AdminLayout>
  );
}
